/**
 * Veterancy and cohesion types and modifiers ported from WarYes
 * Now generated from unit-trait-effects.json data
 */

import type { UnitTraitEffectsMap } from './unit-trait-effects';

export type VeterancyModifier = {
  staticAccuracy?: [string, number];
  motionAccuracy?: [string, number];
  aimTime?: number;
  reloadTime?: number;
  suppressionReceived?: number;
  veterancy: Veterancy;
  suppressRegen?: [string, number];
  dodgeBonus?: [string, number];
  speed?: number; // Percentage modifier (e.g., 10 means +10%)
};

export type CohesionModifier = {
  accuracy: number;
  reloadTime?: number;
  aimTime?: number;
  weaponDispersion?: number; // Multiplier for weapon dispersion
  cohesion: Cohesion;
};

export type TraitModifier = {
  staticAccuracy?: [string, number];
  motionAccuracy?: [string, number];
  aimTime?: number;
  reloadTime?: number;
  suppressionReceived?: number;
  suppressRegen?: [string, number];
  dodgeBonus?: [string, number];
  speed?: number;
  chassisRotationSpeed?: number;
  turretRotationSpeed?: number;
  weaponDispersion?: number;
  weaponDamage?: number; // Multiplier (e.g., 1.15 for +15%)
  timeBetweenShots?: number; // Multiplier (e.g., 0.85 for -15%)
  hitRollECM?: number; // ECM bonus modifier (e.g., -0.1 for EO dazzler)
  opticalStrength?: number; // Additive modifier for optics (e.g., +43)
  stealth?: number; // Additive modifier for stealth/concealment (e.g., +0.5)
  traitName: string; // Required field (e.g., "_choc", "_resolute")
};

export enum Veterancy {
  VET_0 = 'Vet 0',
  VET_1 = 'Vet 1',
  VET_2 = 'Vet 2',
  VET_3 = 'Vet 3',
  ARTY_VET_0 = 'Arty Vet 0',
  ARTY_VET_1 = 'Arty Vet 1',
  ARTY_VET_2 = 'Arty Vet 2',
  ARTY_VET_3 = 'Arty Vet 3',
  PLANE_VET_0 = 'Plane Vet 0',
  PLANE_VET_1 = 'Plane Vet 1',
  PLANE_VET_2 = 'Plane Vet 2',
  PLANE_VET_3 = 'Plane Vet 3',
  HELICOPTER_VET_0 = 'Helicopter Vet 0',
  HELICOPTER_VET_1 = 'Helicopter Vet 1',
  HELICOPTER_VET_2 = 'Helicopter Vet 2',
  HELICOPTER_VET_3 = 'Helicopter Vet 3',
  SF_VET_1 = 'SF Vet 1',
  SF_VET_2 = 'SF Vet 2',
  SF_VET_3 = 'SF Vet 3',
}

/** UI label matching in-game experience ladder (_xp_rookie → Rookie, etc.) */
export function getVeterancyDisplayName(v: Veterancy): string {
  switch (v) {
    case Veterancy.VET_0:
    case Veterancy.ARTY_VET_0:
    case Veterancy.PLANE_VET_0:
    case Veterancy.HELICOPTER_VET_0:
      return "Rookie";
    case Veterancy.VET_1:
    case Veterancy.ARTY_VET_1:
    case Veterancy.PLANE_VET_1:
    case Veterancy.HELICOPTER_VET_1:
    case Veterancy.SF_VET_1:
      return "Trained";
    case Veterancy.VET_2:
    case Veterancy.ARTY_VET_2:
    case Veterancy.PLANE_VET_2:
    case Veterancy.HELICOPTER_VET_2:
    case Veterancy.SF_VET_2:
      return "Veteran";
    case Veterancy.VET_3:
    case Veterancy.ARTY_VET_3:
    case Veterancy.PLANE_VET_3:
    case Veterancy.HELICOPTER_VET_3:
    case Veterancy.SF_VET_3:
      return "Elite";
    default: {
      const _exhaustive: never = v;
      return _exhaustive;
    }
  }
}

export enum Cohesion {
  HIGH = 'High',
  NORMAL = 'Normal',
  MEDIOCRE = 'Mediocre',
  LOW = 'Low',
  ARTY_HIGH = 'High (Arty)',
  ARTY_NORMAL = 'Normal (Arty)',
  ARTY_MEDIOCRE = 'Mediocre (Arty)',
  ARTY_LOW = 'Low (Arty)',
  PLANE_HIGH = 'High (Plane)',
  PLANE_NORMAL = 'Normal (Plane)',
  PLANE_MEDIOCRE = 'Mediocre (Plane)',
  PLANE_LOW = 'Low (Plane)',
}

export enum ExperiencePackDescriptor {
  SF = 'ExperienceLevelsPackDescriptor_XP_pack_SF',
  ARTILLERY = 'ExperienceLevelsPackDescriptor_XP_pack_artillery',
  PLANE = 'ExperienceLevelsPackDescriptor_XP_pack_avion',
  HELICOPTER = 'ExperienceLevelsPackDescriptor_XP_pack_helico',
  STANDARD = 'ExperienceLevelsPackDescriptor_XP_pack_simple_v3',
}

// Lazy-initialized modifier maps
let veterancyModifiersMap: { [key in Veterancy]: VeterancyModifier } | null = null;
let cohesionModifiersMap: { [key in Cohesion]: CohesionModifier } | null = null;
let traitModifiersMap: { [traitName: string]: TraitModifier } | null = null;

/**
 * Initialize modifier maps from unit-trait-effects data
 * This should be called when unit-trait-effects data is loaded from the DataStore
 */
export async function initializeModifierMaps(unitTraitEffectsData: UnitTraitEffectsMap): Promise<void> {
  // Use dynamic import to break circular dependency with modifier-builder.ts
  const { buildVeterancyModifiersMap, buildCohesionModifiersMap, buildTraitModifiersMap } = await import('../utils/modifier-builder');
  veterancyModifiersMap = buildVeterancyModifiersMap(unitTraitEffectsData);
  cohesionModifiersMap = buildCohesionModifiersMap(unitTraitEffectsData);
  traitModifiersMap = buildTraitModifiersMap(unitTraitEffectsData);
}

/**
 * Get the veterancy modifiers map
 * Throws an error if maps haven't been initialized yet
 */
export function getVeterancyModifiersMap(): { [key in Veterancy]: VeterancyModifier } {
  if (!veterancyModifiersMap) {
    throw new Error('Veterancy modifiers map not initialized. Ensure unit-trait-effects data is loaded.');
  }
  return veterancyModifiersMap;
}

/**
 * Get the cohesion modifiers map
 * Throws an error if maps haven't been initialized yet
 */
export function getCohesionModifiersMap(): { [key in Cohesion]: CohesionModifier } {
  if (!cohesionModifiersMap) {
    throw new Error('Cohesion modifiers map not initialized. Ensure unit-trait-effects data is loaded.');
  }
  return cohesionModifiersMap;
}

/**
 * Get the trait modifiers map
 * Throws an error if maps haven't been initialized yet
 */
export function getTraitModifiersMap(): { [traitName: string]: TraitModifier } {
  if (!traitModifiersMap) {
    throw new Error('Trait modifiers map not initialized. Ensure unit-trait-effects data is loaded.');
  }
  return traitModifiersMap;
}

/**
 * Veterancy modifiers map (lazy-initialized getter)
 * Use getVeterancyModifiersMap() for better error handling
 */
export const VETERANCY_MODIFIERS_MAP: {
  [key in Veterancy]: VeterancyModifier;
} = new Proxy({} as { [key in Veterancy]: VeterancyModifier }, {
  get(_target, prop) {
    if (typeof prop === 'string' && prop in getVeterancyModifiersMap()) {
      return getVeterancyModifiersMap()[prop as Veterancy];
    }
    return undefined;
  },
  ownKeys() {
    return Object.keys(getVeterancyModifiersMap()) as (string | symbol)[];
  },
  getOwnPropertyDescriptor(_target, prop) {
    const map = getVeterancyModifiersMap();
    if (typeof prop === 'string' && prop in map) {
      return {
        enumerable: true,
        configurable: true,
        value: map[prop as Veterancy],
      };
    }
    return undefined;
  },
  has(_target, prop) {
    if (typeof prop === 'string') {
      return prop in getVeterancyModifiersMap();
    }
    return false;
  },
});

/**
 * Cohesion modifiers map (lazy-initialized getter)
 * Use getCohesionModifiersMap() for better error handling
 */
export const COHESION_MODIFIERS_MAP: {
  [key in Cohesion]: CohesionModifier;
} = new Proxy({} as { [key in Cohesion]: CohesionModifier }, {
  get(_target, prop) {
    if (typeof prop === 'string' && prop in getCohesionModifiersMap()) {
      return getCohesionModifiersMap()[prop as Cohesion];
    }
    return undefined;
  },
  ownKeys() {
    return Object.keys(getCohesionModifiersMap()) as (string | symbol)[];
  },
  getOwnPropertyDescriptor(_target, prop) {
    const map = getCohesionModifiersMap();
    if (typeof prop === 'string' && prop in map) {
      return {
        enumerable: true,
        configurable: true,
        value: map[prop as Cohesion],
      };
    }
    return undefined;
  },
  has(_target, prop) {
    if (typeof prop === 'string') {
      return prop in getCohesionModifiersMap();
    }
    return false;
  },
});

/**
 * Trait modifiers map (lazy-initialized getter)
 * Use getTraitModifiersMap() for better error handling
 */
export const TRAIT_MODIFIERS_MAP: {
  [traitName: string]: TraitModifier;
} = new Proxy({} as { [traitName: string]: TraitModifier }, {
  get(_target, prop) {
    if (typeof prop === 'string') {
      const map = getTraitModifiersMap();
      return map[prop];
    }
    return undefined;
  },
  ownKeys() {
    return Object.keys(getTraitModifiersMap()) as (string | symbol)[];
  },
  getOwnPropertyDescriptor(_target, prop) {
    const map = getTraitModifiersMap();
    if (typeof prop === 'string' && prop in map) {
      return {
        enumerable: true,
        configurable: true,
        value: map[prop],
      };
    }
    return undefined;
  },
  has(_target, prop) {
    if (typeof prop === 'string') {
      return prop in getTraitModifiersMap();
    }
    return false;
  },
});

export const VETERANCIES: Veterancy[] = [
  Veterancy.VET_0,
  Veterancy.VET_1,
  Veterancy.VET_2,
  Veterancy.VET_3,
];

export const SF_VETERANCIES: Veterancy[] = [
  Veterancy.SF_VET_1,
  Veterancy.SF_VET_2,
  Veterancy.SF_VET_3,
];

export const ARTY_VETERANCIES: Veterancy[] = [
  Veterancy.ARTY_VET_0,
  Veterancy.ARTY_VET_1,
  Veterancy.ARTY_VET_2,
  Veterancy.ARTY_VET_3,
];

export const PLANE_VETERANCIES: Veterancy[] = [
  Veterancy.PLANE_VET_0,
  Veterancy.PLANE_VET_1,
  Veterancy.PLANE_VET_2,
  Veterancy.PLANE_VET_3,
];

export const HELICOPTER_VETERANCIES: Veterancy[] = [
  Veterancy.HELICOPTER_VET_0,
  Veterancy.HELICOPTER_VET_1,
  Veterancy.HELICOPTER_VET_2,
  Veterancy.HELICOPTER_VET_3,
];

export const COHESIONS: Cohesion[] = [
  Cohesion.HIGH,
  Cohesion.NORMAL,
  Cohesion.MEDIOCRE,
  Cohesion.LOW,
];

export const PLANE_COHESIONS: Cohesion[] = [
  Cohesion.PLANE_HIGH,
  Cohesion.PLANE_NORMAL,
  Cohesion.PLANE_MEDIOCRE,
  Cohesion.PLANE_LOW,
];

